import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, Trash2, MapPin, Send } from "lucide-react";
import { ScoutBookmark } from "@/hooks/useFieldScout";
import { toast } from "sonner";
import { TRADE_LABELS } from "@/lib/damageTypes";

interface FieldScoutReviewProps {
  videoBlob: Blob;
  bookmarks: ScoutBookmark[];
  onConfirm: (bookmarks: ScoutBookmark[]) => void;
  onCancel: () => void;
}

export const FieldScoutReview = ({
  videoBlob,
  bookmarks: initialBookmarks,
  onConfirm,
  onCancel
}: FieldScoutReviewProps) => {
  const [bookmarks, setBookmarks] = useState(initialBookmarks);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const url = URL.createObjectURL(videoBlob);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [videoBlob]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const seekToBookmark = (timestamp: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = timestamp;
    }
  };

  const removeBookmark = (id: string) => {
    setBookmarks(prev => prev.filter(b => b.id !== id));
    toast.success('Bookmark removed');
  };

  const handleConfirm = () => {
    if (bookmarks.length === 0) {
      toast.error('Keep at least one marked spot');
      return;
    }
    onConfirm(bookmarks);
  };

  const getDamageTypeColor = (trade?: string) => {
    switch (trade) {
      case 'roofing': return 'bg-blue-500';
      case 'siding': return 'bg-purple-500';
      case 'general': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      {/* Header */}
      <div className="border-b p-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Review Marked Spots</h2>
          <p className="text-sm text-muted-foreground">
            {bookmarks.length} {bookmarks.length === 1 ? 'spot' : 'spots'} marked
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Video Preview */}
      <div className="bg-black">
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          className="w-full max-h-64 object-contain"
        />
      </div>

      {/* Bookmarks List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {bookmarks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No bookmarks remaining</p>
            <p className="text-sm">Add at least one to continue</p>
          </div>
        ) : (
          bookmarks.map((bookmark, index) => (
            <Card key={bookmark.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="font-mono">
                      {formatTime(bookmark.timestamp)}
                    </Badge>
                    {bookmark.trade && (
                      <Badge className={getDamageTypeColor(bookmark.trade)}>
                        {TRADE_LABELS[bookmark.trade]}
                      </Badge>
                    )}
                    {bookmark.damageType && (
                      <Badge variant="secondary">
                        {bookmark.damageType.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                    )}
                    {bookmark.gps && (
                      <MapPin className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Spot #{index + 1}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => seekToBookmark(bookmark.timestamp)}
                  >
                    View
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeBookmark(bookmark.id)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Footer Actions */}
      <div className="border-t p-4 space-y-2">
        <Button 
          className="w-full gap-2" 
          size="lg"
          onClick={handleConfirm}
          disabled={bookmarks.length === 0}
        >
          <Send className="w-5 h-5" />
          Analyze {bookmarks.length} Marked {bookmarks.length === 1 ? 'Spot' : 'Spots'}
        </Button>
        <Button 
          variant="outline" 
          className="w-full" 
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
